import { readCookie, writeCookie } from '@ui/lib/cookies'

export interface UtmData {
  [key: string]: string
}

export const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
]

export const UTM_COOKIE = 'utm_params'

export function getUtmParams(url: URL): UtmData {
  const utmData: UtmData = {}
  UTM_PARAMS.forEach((param) => {
    const value = url.searchParams.get(param)
    if (value) utmData[param] = value
  })
  return utmData
}

export function captureUtmFromUrl() {
  if (typeof window === 'undefined') return
  const fromUrl = getUtmParams(new URL(window.location.href))
  if (Object.keys(fromUrl).length === 0) return

  const existing = parseUtmCookie(readCookie(UTM_COOKIE))
  const merged = { ...existing, ...fromUrl }
  writeCookie(UTM_COOKIE, JSON.stringify(merged))
}

export function replacePlaceholdersWithUtmData(
  url: string,
  utmData: UtmData,
): string {
  let updatedUrl = url

  // First replace all available UTM parameters
  Object.entries(utmData).forEach(([key, value]) => {
    const placeholder = `{${key}}`
    const encodedValue = encodeURIComponent(String(value))
    updatedUrl = updatedUrl.replace(placeholder, encodedValue)
  })

  // Then remove any remaining UTM parameters that weren't in utmData
  UTM_PARAMS.forEach((param) => {
    const placeholder = `{${param}}`
    if (updatedUrl.includes(placeholder)) {
      // Remove the parameter and its value completely
      updatedUrl = updatedUrl.replace(
        new RegExp(`&${param}=${placeholder}|${param}=${placeholder}&?`),
        '',
      )
    }
  })

  // Clean up the URL by removing trailing '&' or '?' if no parameters remain
  updatedUrl = updatedUrl.replace(/[?&]$/, '')

  return updatedUrl
}

// Expects an already-decoded cookie value. Both readCookie() and Next.js's
// req.cookies return decoded strings, so decoding again here would throw on
// values that contain a literal '%' (e.g. utm_campaign=50%off).
export function parseUtmCookie(decodedCookie?: string): UtmData {
  if (!decodedCookie) return {}

  try {
    const parsed = JSON.parse(decodedCookie)
    // JSON.parse('null') / 'true' / '5' all succeed but aren't UtmData. The
    // !decodedCookie guard above only catches undefined/empty, so a literal
    // `utm_params=null` cookie (bots and corrupt clients send these) would
    // return null and make callers' Object.entries(utmData) throw — a silent
    // 500 on /sign-up. Only a plain object is valid.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }
    return parsed
  } catch (error) {
    console.error('Error parsing UTM cookie:', error)
    return {}
  }
}
